import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useEditProjectMutation } from "../../../../features/projectApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import Button from "../../../ui/molecules/button";
import Input from "../../../ui/molecules/input";
import { Spinner } from "../../../ui/molecules/spinner";
import { ProjectDTO } from "../dto";
import { ProjectEmailsFormData, projectEmailsSchema } from "../editProject/validation";
import { useSendVerificationMailMutation } from "../../../../features/mailsApi";

interface Props {
    project: ProjectDTO
}

export function ProjectSettings({ project }: Props) {
    const mailsState = useAppSelector(state => state.mails)
    const dispatch = useAppDispatch()

    const [verifyEmail, { isLoading: isVerifyingEmail }] = useSendVerificationMailMutation()
    const handleVerifyEmail = async () => {
        //TODO see error handling
        try {
            await verifyEmail(project.emailFrom!).unwrap()
            dispatch(addBannerWithTimeout({ message: "Email di verifica inviata con successo", variant: "success" }))
        } catch (error) {
            let message = "Errore durante l'invio dell'email di verifica"
            const _error = error as { status: number, data: string }
            if (_error.status === 429) {
                message = "Limite di invio email raggiunto"
            } else if (_error.data === 'email already verified') {
                message = "L'email è già stata verificata"
            }
            dispatch(addBannerWithTimeout({ message, variant: "error" }))
        }
    }

    const form = useForm<ProjectEmailsFormData>({
        resolver: zodResolver(projectEmailsSchema),
    })

    useEffect(() => {
        if (project) {
            form.reset({
                emailFrom: project.emailFrom ?? undefined,
                nameFrom: project.nameFrom ?? undefined
            })
        }
    }, [project])

    const [updateProject, { isLoading: isUpdatingProject }] = useEditProjectMutation()
    const handleUpdateProject = async () => {
        try {
            await updateProject({ id: project.id, data: form.getValues() })
            dispatch(addBannerWithTimeout({ message: "Impostazioni email aggiornate con successo", variant: "success" }))
        } catch (error) {
            dispatch(addBannerWithTimeout({ message: "Errore durante l'aggiornamento delle impostazioni email", variant: "error" }))
        }
    }

    return (
        <div className="flex flex-col w-full">
            <div key={2} className=' flex flex-col w-full py-8 px-4 gap-4'>
                <div className='flex flex-col gap-3 '>
                    <Input
                        label='E-mail del mittente'
                        value={form.watch('emailFrom') ?? undefined}
                        onChange={(e) => form.setValue('emailFrom', e.target.value)}
                        placeholder="Specificare l'indirizzo email di chi invia le mail"
                        error={!!form.formState.errors.emailFrom}
                        supportingText={form.formState.errors.emailFrom?.message}
                    />
                </div>
                <div className='flex flex-col gap-3'>
                    <Input
                        label='Nome del mittente'
                        value={form.watch('nameFrom') ?? undefined}
                        onChange={(e) => form.setValue('nameFrom', e.target.value)}
                        placeholder="Specificare il nome di chi invia le mail"
                        error={!!form.formState.errors.nameFrom}
                        supportingText={form.formState.errors.nameFrom?.message}
                    />
                </div>
            </div>
            <div className=' flex justify-end items-center w-full border-t gap-2 pt-4 px-2 border-neutral-200'>
                {
                    mailsState.emailSettingsTab !== 1 &&
                    <>
                        {isVerifyingEmail ?
                            <Spinner /> :
                            <Button
                                label='Verifica email'
                                size={'sm'}
                                iconPosition={'left'}
                                variant={'link'}
                                color={'blue'}
                                onClick={handleVerifyEmail}
                            />
                        }
                        {
                            isUpdatingProject ?
                                <Spinner /> :
                                <Button
                                    onClick={() => form.handleSubmit(handleUpdateProject)()}
                                    label={'Salva impostazioni'}
                                    size={'sm'}
                                    iconPosition={'left'}
                                    variant={'solid'}
                                    color={'blue'}
                                />
                        }
                    </>
                }
            </div>
        </div>
    )
}